// Minimal .xlsx writer and reader built on zip.js.
//
// Writer: text columns (EAN, Article, Location, ...) are written as text cells
// so leading zeros survive; quantities and values as numbers. Sheets that
// exceed Excel's 1,048,576-row limit are split into "Name (2)", "Name (3)"...
// Reader: returns every sheet's rows as strings (no number conversion), so
// codes keep their zeros when the source cell was text.

import { createZip, openZip } from './zip.js';

export const EXCEL_MAX_ROWS = 1048576;

const XML_HEADER = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n';

function escapeXml(value) {
  return String(value)
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function columnLetter(index) {
  let n = index + 1;
  let letters = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    letters = String.fromCharCode(65 + rem) + letters;
    n = Math.floor((n - 1) / 26);
  }
  return letters;
}

function sheetSafeName(name, used) {
  let base = String(name || 'Sheet').replace(/[[\]:*?/\\]/g, ' ').trim().slice(0, 31) || 'Sheet';
  let candidate = base;
  let n = 2;
  while (used.has(candidate.toLowerCase())) {
    const suffix = ` (${n})`;
    candidate = base.slice(0, 31 - suffix.length) + suffix;
    n += 1;
  }
  used.add(candidate.toLowerCase());
  return candidate;
}

// Style ids (see stylesXml): 0 normal, 1 header, 2 money, 3 qty, 4 title, 5 text, 6 note
const STYLE = { normal: 0, header: 1, money: 2, qty: 3, title: 4, text: 5, note: 6 };

function stylesXml() {
  return `${XML_HEADER}<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<numFmts count="2"><numFmt numFmtId="164" formatCode="#,##0.00"/><numFmt numFmtId="165" formatCode="#,##0.###"/></numFmts>
<fonts count="4"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="14"/><name val="Calibri"/></font><font><i/><sz val="10"/><color rgb="FF8A1C1C"/><name val="Calibri"/></font></fonts>
<fills count="3"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill><fill><patternFill patternType="solid"><fgColor rgb="FFE8ECF4"/><bgColor indexed="64"/></patternFill></fill></fills>
<borders count="2"><border><left/><right/><top/><bottom/><diagonal/></border><border><left/><right/><top/><bottom style="thin"><color rgb="FF9AA5B8"/></bottom><diagonal/></border></borders>
<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
<cellXfs count="7">
<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
<xf numFmtId="0" fontId="1" fillId="2" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1"/>
<xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
<xf numFmtId="165" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
<xf numFmtId="0" fontId="2" fillId="0" borderId="0" xfId="0" applyFont="1"/>
<xf numFmtId="49" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
<xf numFmtId="0" fontId="3" fillId="0" borderId="0" xfId="0" applyFont="1"/>
</cellXfs>
<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`;
}

function textCell(ref, value, style) {
  if (value === null || value === undefined || value === '') return '';
  return `<c r="${ref}" t="inlineStr" s="${style}"><is><t xml:space="preserve">${escapeXml(value)}</t></is></c>`;
}

function numberCell(ref, value, style) {
  if (value === null || value === undefined || value === '' || !Number.isFinite(Number(value))) return '';
  return `<c r="${ref}" s="${style}"><v>${Number(value)}</v></c>`;
}

/**
 * Builds sheet XML strings for one logical sheet, split if needed.
 * sheet = { name, title?: string[], notes?: string[], columns: [{ label, key, type: 'text'|'qty'|'money'|'number', width? }], rows: object[] }
 */
function buildSheetParts(sheet) {
  const prefixRows = [];
  if (sheet.title?.length) for (const line of sheet.title) prefixRows.push({ kind: 'title', value: line });
  if (sheet.notes?.length) for (const line of sheet.notes) prefixRows.push({ kind: 'note', value: line });
  if (prefixRows.length) prefixRows.push({ kind: 'blank' });

  const headerRowIndex = prefixRows.length + 1; // 1-based
  const capacity = EXCEL_MAX_ROWS - headerRowIndex;
  const chunks = [];
  for (let start = 0; start < Math.max(sheet.rows.length, 1); start += capacity) {
    chunks.push(sheet.rows.slice(start, start + capacity));
  }

  return chunks.map((rows, chunkIndex) => {
    const out = [];
    out.push(
      `${XML_HEADER}<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">`,
    );
    out.push(
      `<sheetViews><sheetView workbookViewId="0"><pane ySplit="${headerRowIndex}" topLeftCell="A${headerRowIndex + 1}" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>`,
    );
    out.push('<cols>');
    sheet.columns.forEach((column, index) => {
      const width = column.width || Math.min(48, Math.max(10, column.label.length + 2));
      out.push(`<col min="${index + 1}" max="${index + 1}" width="${width}" customWidth="1"/>`);
    });
    out.push('</cols><sheetData>');

    let r = 1;
    for (const prefix of prefixRows) {
      if (prefix.kind === 'blank') {
        r += 1;
        continue;
      }
      const style = prefix.kind === 'title' ? STYLE.title : STYLE.note;
      const value = chunkIndex > 0 && prefix.kind === 'title' ? `${prefix.value} (continued, part ${chunkIndex + 1})` : prefix.value;
      out.push(`<row r="${r}">${textCell(`A${r}`, value, style)}</row>`);
      r += 1;
    }

    out.push(`<row r="${r}">`);
    sheet.columns.forEach((column, index) => out.push(textCell(`${columnLetter(index)}${r}`, column.label, STYLE.header)));
    out.push('</row>');
    r += 1;

    for (const row of rows) {
      out.push(`<row r="${r}">`);
      sheet.columns.forEach((column, index) => {
        const ref = `${columnLetter(index)}${r}`;
        const value = typeof column.value === 'function' ? column.value(row) : row[column.key];
        if (column.type === 'money') out.push(numberCell(ref, value, STYLE.money));
        else if (column.type === 'qty' || column.type === 'number') out.push(numberCell(ref, value, STYLE.qty));
        else out.push(textCell(ref, value, STYLE.text));
      });
      out.push('</row>');
      r += 1;
    }
    out.push('</sheetData>');
    const lastCol = columnLetter(sheet.columns.length - 1);
    out.push(`<autoFilter ref="A${headerRowIndex}:${lastCol}${Math.max(headerRowIndex, r - 1)}"/>`);
    out.push('</worksheet>');
    return { name: chunkIndex === 0 ? sheet.name : `${sheet.name} (${chunkIndex + 1})`, xml: out.join(''), autoFilterRow: headerRowIndex, lastRow: r - 1, lastCol };
  });
}

/** @returns {Promise<Uint8Array>} */
export async function writeXlsx(sheets) {
  const used = new Set();
  const parts = [];
  for (const sheet of sheets) {
    for (const part of buildSheetParts(sheet)) {
      parts.push({ ...part, safeName: sheetSafeName(part.name, used) });
    }
  }

  const files = [];
  files.push({
    name: '[Content_Types].xml',
    data: `${XML_HEADER}<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>${parts
      .map((_, i) => `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`)
      .join('')}</Types>`,
  });
  files.push({
    name: '_rels/.rels',
    data: `${XML_HEADER}<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>`,
  });
  files.push({
    name: 'xl/workbook.xml',
    data: `${XML_HEADER}<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets>${parts
      .map((p, i) => `<sheet name="${escapeXml(p.safeName)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`)
      .join('')}</sheets><definedNames>${parts
      .map((p, i) => `<definedName name="_xlnm._FilterDatabase" localSheetId="${i}" hidden="1">'${escapeXml(p.safeName).replace(/'/g, "''")}'!$A$${p.autoFilterRow}:$${p.lastCol}$${Math.max(p.autoFilterRow, p.lastRow)}</definedName>`)
      .join('')}</definedNames></workbook>`,
  });
  files.push({
    name: 'xl/_rels/workbook.xml.rels',
    data: `${XML_HEADER}<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">${parts
      .map((_, i) => `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`)
      .join('')}<Relationship Id="rId${parts.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>`,
  });
  files.push({ name: 'xl/styles.xml', data: stylesXml() });
  parts.forEach((part, i) => files.push({ name: `xl/worksheets/sheet${i + 1}.xml`, data: part.xml }));

  return createZip(files);
}

// --- Reader --------------------------------------------------------------------

function decodeXml(text) {
  return text
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&#(\d+);/g, (_, code) => String.fromCodePoint(Number(code)))
    .replace(/&#x([0-9a-f]+);/gi, (_, code) => String.fromCodePoint(parseInt(code, 16)))
    .replace(/&amp;/g, '&');
}

function textOf(xml) {
  // Concatenate every <t> inside (handles rich text runs); ignore phonetic <rPh>.
  const withoutPhonetic = xml.replace(/<rPh\b[\s\S]*?<\/rPh>/g, '');
  let out = '';
  const re = /<t(?:\s[^>]*)?>([\s\S]*?)<\/t>|<t(?:\s[^>]*)?\/>/g;
  let m;
  while ((m = re.exec(withoutPhonetic))) out += m[1] ? decodeXml(m[1]) : '';
  return out;
}

function columnIndex(ref) {
  const letters = ref.replace(/\d+/g, '');
  let n = 0;
  for (let i = 0; i < letters.length; i += 1) n = n * 26 + (letters.charCodeAt(i) - 64);
  return n - 1;
}

/** Numbers stored by Excel keep their digits; scientific notation is expanded for whole numbers. */
function numberText(raw) {
  const text = raw.trim();
  if (/e/i.test(text)) {
    const value = Number(text);
    if (Number.isFinite(value) && Number.isInteger(value) && Math.abs(value) <= Number.MAX_SAFE_INTEGER) {
      return String(value);
    }
  }
  return text;
}

/**
 * Reads an .xlsx file. Returns [{ name, rows: [{ rowNumber, cells: string[] }] }].
 * Legacy .xls (binary) files are not supported - save them as .xlsx or CSV.
 */
export async function readXlsx(bytes) {
  const zip = openZip(bytes);
  const workbook = await zip.readText('xl/workbook.xml');
  if (!workbook) throw new Error('This file is not an .xlsx workbook. Save it as .xlsx or CSV and try again.');
  const rels = (await zip.readText('xl/_rels/workbook.xml.rels')) || '';
  const relTargets = new Map();
  for (const m of rels.matchAll(/<Relationship\b[^>]*>/g)) {
    const tag = m[0];
    const id = /Id="([^"]+)"/.exec(tag)?.[1];
    const target = /Target="([^"]+)"/.exec(tag)?.[1];
    if (id && target) relTargets.set(id, target.replace(/^\/?xl\//, '').replace(/^\//, ''));
  }

  const shared = [];
  const sharedXml = await zip.readText('xl/sharedStrings.xml');
  if (sharedXml) for (const m of sharedXml.matchAll(/<si\b[^>]*>([\s\S]*?)<\/si>/g)) shared.push(textOf(m[1]));

  const sheets = [];
  for (const m of workbook.matchAll(/<sheet\b[^>]*>/g)) {
    const tag = m[0];
    const name = decodeXml(/name="([^"]*)"/.exec(tag)?.[1] || 'Sheet');
    const relId = /r:id="([^"]+)"/.exec(tag)?.[1];
    const target = relTargets.get(relId);
    if (!target) continue;
    const xml = await zip.readText(`xl/${target}`);
    if (!xml) continue;

    const rows = [];
    for (const rowMatch of xml.matchAll(/<row\b([^>]*)>([\s\S]*?)<\/row>/g)) {
      const rowNumber = Number(/\br="(\d+)"/.exec(rowMatch[1])?.[1]) || rows.length + 1;
      const cells = [];
      for (const cellMatch of rowMatch[2].matchAll(/<c\b([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g)) {
        const attrs = cellMatch[1];
        const body = cellMatch[2] || '';
        const ref = /\br="([A-Z]+\d+)"/.exec(attrs)?.[1];
        const index = ref ? columnIndex(ref) : cells.length;
        const type = /\bt="([^"]+)"/.exec(attrs)?.[1] || 'n';
        const v = /<v>([\s\S]*?)<\/v>/.exec(body)?.[1];
        let value = '';
        if (type === 's') value = shared[Number(v)] ?? '';
        else if (type === 'inlineStr') value = textOf(body);
        else if (type === 'str') value = v != null ? decodeXml(v) : '';
        else if (type === 'b') value = v === '1' ? 'TRUE' : 'FALSE';
        else if (type === 'e') value = '';
        else value = v != null ? numberText(decodeXml(v)) : '';
        while (cells.length < index) cells.push('');
        cells[index] = value.trim();
      }
      if (cells.some((c) => c !== '')) rows.push({ rowNumber, cells });
    }
    sheets.push({ name, rows });
  }
  return sheets;
}
